import * as React from 'react';
import { useEffect, useState } from 'react';
import glossary from 'helpers/glossaries/fr';
import { DataTypes as DT } from 'business/definitions';
import { translateBlock, translateBlockValue } from 'i18n';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { formatDisplay } from 'helpers/formatHelper';
import { capitalize } from 'helpers/helpers';
import { snakify } from 'helpers/syntaxHelper';
import { trackEvent } from 'helpers/trackerHelpers';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Typography from '@mui/material/Typography';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useSelector } from 'store/hooks';
import TooltipIconButton from 'components/TooltipIconButton';
import Flex from 'ui/Flex/Flex';
import { isKeyOf } from 'helpers/types/utils';
import CellarViewIndicators from './CellarViewIndicators';
import { AvailableFields, AvailableTable, FieldInfo } from '../types';
import './_cellar.scss';
import {
    useGetFieldSynopsis,
    useToggleFieldVisibilityMutation,
} from '../hooks/tableSynopsis';
import CellarCardLoading from './CellarCardLoading';

type Props = {
    name: AvailableFields;
    table: AvailableTable;
};

const CellarCardRaw = ({ name, table }: Props) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const isSupportUser = useSelector((state) => state.vision.isSupportUser);
    const isAdminUser = useSelector((state) => state.vision.isAdmin);
    const [fieldDetails, setFieldDetails] = useState<FieldInfo | undefined>();

    const { data, isLoading } = useGetFieldSynopsis(table, name);

    const toggleVisibility = (): void => {
        setFieldDetails((prevState) =>
            prevState
                ? {
                      ...prevState,
                      isVisibleForBind: !prevState.isVisibleForBind,
                  }
                : prevState,
        );
    };

    const { mutate: toggleFieldVisibility } = useToggleFieldVisibilityMutation(
        table,
        name,
        toggleVisibility,
    );

    useEffect(() => {
        if (!isLoading && data) {
            setFieldDetails(data);
        }
    }, [data, isLoading]);

    const isFieldVisible = fieldDetails?.isVisibleForBind;

    const toggleIsVisible = (): void => {
        trackEvent(
            isFieldVisible
                ? 'cellar/table/fields/hide'
                : 'cellar/table/fields/show',
        );
        toggleFieldVisibility({
            table,
            name,
        });
    };

    const hiddenClassName =
        isFieldVisible !== undefined && !isFieldVisible
            ? 'cellar-card-hidden'
            : '';

    const getFieldDetail = (message: string, value?: number) => {
        if (value == null) {
            return null;
        }
        const formattedValue =
            fieldDetails?.type === DT.date
                ? formatDisplay(new Date(value), DT.date)
                : formatDisplay(value, DT.float);
        return (
            <div className={`padded ${hiddenClassName}`} key={message}>
                {capitalize(message)} : {formattedValue}
            </div>
        );
    };

    const viewIndicator = (): void => {
        const field = snakify(name);

        navigate(
            `/explore?dimensions=${table}.${field}&metrics=${table}|number`,
        );
    };

    const className = fieldDetails?.isCustom
        ? 'cellar-card e2e-cellar-card-complex'
        : 'cellar-card';

    const visibilityButton = isFieldVisible ? (
        <Visibility />
    ) : (
        <VisibilityOff />
    );

    const canUpdateFieldVisibility =
        (isSupportUser || isAdminUser) && !fieldDetails?.isCustom;

    const visibilityTooltip = isFieldVisible
        ? t('cellar.markFieldAsHidden')
        : t('cellar.markFieldAsVisible');

    return !fieldDetails ? (
        <CellarCardLoading />
    ) : (
        <Card className={className} elevation={1}>
            <CardContent>
                <Flex align="center" justify="space-between">
                    <Typography
                        color="primary"
                        className={hiddenClassName}
                        variant="body1Bold"
                    >
                        {capitalize(translateBlock(`${table}.${name}`))}
                    </Typography>
                    {canUpdateFieldVisibility && (
                        <TooltipIconButton
                            icon={visibilityButton}
                            onClick={toggleIsVisible}
                            tooltip={visibilityTooltip}
                            className={hiddenClassName}
                        />
                    )}
                </Flex>
                <div className="card-header">
                    <Typography variant="body1" className={hiddenClassName}>
                        {t(`cellar.${fieldDetails.type}`)}
                    </Typography>
                    {fieldDetails.isCustom && (
                        <div className="calculated">{t('cellar.computed')}</div>
                    )}
                </div>
                {isKeyOf<object>(glossary, name) && (
                    <Typography variant="body1" className={hiddenClassName}>
                        {glossary[name]}
                    </Typography>
                )}
                <div>
                    {fieldDetails.ratioEmptyRows != null && (
                        <div className={`padded ${hiddenClassName}`}>
                            {t('cellar.emptyValues')} :{' '}
                            {formatDisplay(
                                fieldDetails.ratioEmptyRows,
                                DT.fraction,
                            )}
                        </div>
                    )}
                    {['minimum', 'maximum', 'average'].map((key) =>
                        getFieldDetail(
                            t(`common.${key}`),
                            fieldDetails[
                                key as keyof Pick<
                                    FieldInfo,
                                    'minimum' | 'maximum' | 'average'
                                >
                            ],
                        ),
                    )}
                    {fieldDetails.firsts && (
                        <div className={`padded ${hiddenClassName}`}>
                            {capitalize(t('cellar.examples'))} :{' '}
                            {fieldDetails.firsts
                                .map((w: string) =>
                                    translateBlockValue(w.toUpperCase()),
                                )
                                .join(', ')}
                        </div>
                    )}
                </div>
            </CardContent>
            <CardActions className="cellar-card-actions">
                <CellarViewIndicators
                    viewIndicators={viewIndicator}
                    disabled={isFieldVisible === false}
                />
            </CardActions>
        </Card>
    );
};

export default CellarCardRaw;
